// Libraries.
import { defaults } from 'lodash/fp'
import { call, select, takeEvery } from 'redux-saga/effects'
// Other plugins.
import { getConnectionInfo } from '../../../auth/interface/selectors'
// Logs
import { logManager } from '../../../logs'
import * as actionTypes from '../../interface/actionTypes'
import { getNotificationConfig } from '../../interface/selectors'
import * as requests from '../requests'

// Get the logger
const log = logManager.getLogger('NOTIFICATIONS')

/**
 * Register for apple push notification
 * @method applePushRegistrationEntry
 */
export function * applePushRegistrationEntry () {
  yield takeEvery(actionTypes.REGISTER_APPLE_PUSH_NOTIFICATION, registerPushDeviceToken)
}

/**
 * Register for android push notification
 * @method androidPushRegistrationEntry
 */
export function * androidPushRegistrationEntry () {
  yield takeEvery(actionTypes.REGISTER_ANDROID_PUSH_NOTIFICATION, registerPushDeviceToken)
}

/**
 * Saga for registering a device token for push notifications.
 * @method registerPushDeviceToken
 */
function * registerPushDeviceToken (action) {
  // Wait for a registration action.
  const connection = yield select(getConnectionInfo)
  const { pushRegistration } = yield select(getNotificationConfig)

  // If a push registration endpoint was configured, use that instead of default.
  if (pushRegistration) {
    connection.server = defaults(connection.server, pushRegistration)
    connection.protocol = defaults(connection.protocol, pushRegistration)
    connection.port = defaults(connection.port, pushRegistration)
    connection.version = defaults(connection.version, pushRegistration)
  }

  log.info('Registering device token for PUSH notifications...')
  const response = yield call(requests.pushNotificationsRegistration, connection, {
    ...action.payload
  })

  if (response.error) {
    // Failed to create registration, reject the deferred result.
    yield call([action.meta.deferred, 'reject'], {
      error: response.text
    })
  } else {
    // Successfully created registration, resolve the deferred result and respond with the registration.
    yield call([action.meta.deferred, 'resolve'], response.registration)
  }
}

/**
 * Unregister for apple push notification
 * @method applePushUnregisterEntry
 */
export function * applePushUnregisterEntry () {
  yield takeEvery(actionTypes.UNREGISTER_APPLE_PUSH_NOTIFICATION, unregisterPushDeviceToken)
}

/**
 * Unregister for android push notification.
 * @method androidPushUnregisterEntry
 */
export function * androidPushUnregisterEntry () {
  yield takeEvery(actionTypes.UNREGISTER_ANDROID_PUSH_NOTIFICATION, unregisterPushDeviceToken)
}

/**
 * Saga for deregistering a device token for push notifications.
 * @method deregisterPushDeviceToken
 */
function * unregisterPushDeviceToken (action) {
  const connection = yield select(getConnectionInfo)
  const { pushRegistration } = yield select(getNotificationConfig)

  // If a push registration endpoint was configured, use that instead of default.
  if (pushRegistration) {
    connection.server = defaults(connection.server, pushRegistration)
    connection.protocol = defaults(connection.protocol, pushRegistration)
    connection.port = defaults(connection.port, pushRegistration)
    connection.version = defaults(connection.version, pushRegistration)
  }

  log.info('Un-registering device token for PUSH notifications...')
  const response = yield call(requests.pushNotificationsDeRegistration, connection, {
    registration: action.payload.registration
  })
  if (response.error) {
    yield call([action.meta.deferred, 'reject'], {
      error: response.text
    })
  } else {
    yield call([action.meta.deferred, 'resolve'], undefined)
  }
}
